import type {
  SelectablePlanKey,
  TierLimitsCatalog,
  TierStripePrices
} from '../config/subscriptionTiers'
import { makeApiRequest } from './api.service'
export interface SubscriptionResponse {
  clientSecret?: string
  paymentComplete?: boolean
  subscriptionId: string
  tier: SelectablePlanKey
}
export interface BillingInfo {
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
  hasActiveSubscription: boolean
  hasStripeCustomer: boolean
  tier: string
  subscriptionValidTo: string | null
  subscriptionAccessBlocked: boolean
  defaultPaymentMethod: {
    type?: 'card' | 'au_becs_debit'
    last4: string
    brand: string
    exp_month?: number | null
    exp_year?: number | null
  } | null
  /** Set by webhook on invoice.payment_succeeded; cleared on success */
  billingIssue: boolean
  lastPaymentFailureAt: string | null
  paymentFailureMessage: string | null
}
export interface TierLimitsApiPayload {
  limits: TierLimitsCatalog
  stripePrices: TierStripePrices
}
export interface ChangePlanResponse {
  useCreatePayment?: boolean
  success?: boolean
  message?: string
}
function defaultBillingInfo(): BillingInfo {
  return {
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
    hasActiveSubscription: false,
    hasStripeCustomer: false,
    tier: '',
    subscriptionValidTo: null,
    subscriptionAccessBlocked: false,
    defaultPaymentMethod: null,
    billingIssue: false,
    lastPaymentFailureAt: null,
    paymentFailureMessage: null
  }
}
export class BillingService {
  /** Tier caps from Parameter Store plus Stripe-derived prices (GET /tierlimits). */
  async fetchTierLimitsCatalog(): Promise<TierLimitsApiPayload> {
    const response = await makeApiRequest({
      path: '/tierlimits',
      method: 'GET'
    })
    if (!response.success || response.data?.limits == null || response.data?.stripePrices == null) {
      const err = typeof response.data?.error === 'string' ? response.data.error : null
      throw new Error(err || 'Unable to load plan limits.')
    }
    return response.data as TierLimitsApiPayload
  }
  async changeTier(tier: SelectablePlanKey, message?: any) {
    try {
      const response = await makeApiRequest(
        {
          path: '/billing/changetier',
          method: 'POST',
          body: { tier }
        },
        message
      )
      if (!response.success) {
        throw new Error(response.data?.message || 'Failed to change plan')
      }
      return response.data
    } catch (error) {
      throw error
    }
  }
  async createPaymentIntent(
    tier: SelectablePlanKey,
    returnUrl?: string
  ): Promise<SubscriptionResponse> {
    try {
      const response = await makeApiRequest({
        path: '/billing/createpayment',
        method: 'POST',
        body: { tier, returnUrl: returnUrl || window.location.origin }
      })
      if (!response.success) {
        throw new Error(response.data?.message || 'Failed to create subscription')
      }
      return response.data
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to initialize subscription')
    }
  }
  async fetchSubscriptionGateState(options?: { sync?: boolean }): Promise<{
    tier: string
    subscriptionValidTo: string | null
    subscriptionAccessBlocked: boolean
  } | null> {
    try {
      const path = options?.sync ? '/billing/status?sync=1' : '/billing/status'
      const response = await makeApiRequest({
        path,
        method: 'GET'
      })
      if (!response.success || response.data == null) {
        return null
      }
      const data = response.data
      return {
        tier: data.tier || '',
        subscriptionValidTo: data.subscriptionValidTo || null,
        subscriptionAccessBlocked: !!data.subscriptionAccessBlocked
      }
    } catch (error: any) {
      return null
    }
  }
  async getBillingInfo(options?: { sync?: boolean }): Promise<BillingInfo> {
    try {
      const path = options?.sync ? '/billing/status?sync=1' : '/billing/status'
      const response = await makeApiRequest({
        path,
        method: 'GET'
      })
      if (!response.success || response.data == null) {
        return defaultBillingInfo()
      }
      const data = response.data
      return {
        cancelAtPeriodEnd: !!data.cancelAtPeriodEnd,
        currentPeriodEnd: data.currentPeriodEnd || null,
        hasActiveSubscription: !!data.hasActiveSubscription,
        hasStripeCustomer: !!data.hasStripeCustomer,
        tier: data.tier || '',
        subscriptionValidTo: data.subscriptionValidTo || null,
        subscriptionAccessBlocked: !!data.subscriptionAccessBlocked,
        defaultPaymentMethod: data.defaultPaymentMethod ?? null,
        billingIssue: !!data.billingIssue,
        lastPaymentFailureAt: data.lastPaymentFailureAt || null,
        paymentFailureMessage: data.paymentFailureMessage || null
      }
    } catch (error: any) {
      return defaultBillingInfo()
    }
  }
  async changePlan(tier: SelectablePlanKey, message?: any): Promise<ChangePlanResponse> {
    const response = await makeApiRequest(
      {
        path: '/billing/changeplan',
        method: 'POST',
        body: { tier }
      },
      message
    )
    if (!response.success) {
      const msg = response.data?.message ?? 'Failed to change plan'
      throw new Error(msg)
    }
    return response.data as ChangePlanResponse
  }
  async keepSubscription(message?: any): Promise<{
    success: boolean
    message?: string
  }> {
    try {
      const response = await makeApiRequest(
        {
          path: '/billing/cancel',
          method: 'POST',
          body: { undo: true }
        },
        message
      )
      if (!response.success) {
        throw new Error(response.data?.message || 'Failed to keep subscription')
      }
      return response.data
    } catch (error: any) {
      throw error
    }
  }
  async cancelSubscription(message?: any): Promise<{
    success: boolean
    message?: string
    currentPeriodEnd?: string
  }> {
    try {
      const response = await makeApiRequest(
        {
          path: '/billing/cancel',
          method: 'POST',
          body: {}
        },
        message
      )
      if (!response.success) {
        throw new Error(response.data?.message || 'Failed to cancel subscription')
      }
      return response.data
    } catch (error: any) {
      throw error
    }
  }
  async createSetupIntent(message?: any): Promise<{
    clientSecret: string
  }> {
    const response = await makeApiRequest(
      { path: '/billing/setuppaymentmethod', method: 'POST', body: {} },
      message
    )
    if (!response.success) {
      throw new Error(response.data?.message || 'Failed to create setup session')
    }
    return response.data
  }
  async setDefaultPaymentMethod(
    paymentMethodId: string,
    message?: any
  ): Promise<{
    success: boolean
    message?: string
    invoicePaid?: boolean | null
  }> {
    const response = await makeApiRequest(
      { path: '/billing/defaultpaymentmethod', method: 'POST', body: { paymentMethodId } },
      message
    )
    if (!response.success) {
      throw new Error(response.data?.message || 'Failed to update payment method')
    }
    return response.data
  }
  async listPaymentMethods(): Promise<{
    paymentMethods: PaymentMethodInfo[]
  }> {
    const response = await makeApiRequest({ path: '/billing/paymentmethods', method: 'GET' })
    if (!response.success || response.data == null) {
      return { paymentMethods: [] }
    }
    return {
      paymentMethods: Array.isArray(response.data.paymentMethods)
        ? response.data.paymentMethods
        : []
    }
  }
  async listInvoices(): Promise<{
    invoices: BillingInvoiceRow[]
  }> {
    const response = await makeApiRequest({ path: '/billing/invoices', method: 'GET' })
    if (!response.success || response.data == null) {
      return { invoices: [] }
    }
    return {
      invoices: Array.isArray(response.data.invoices) ? response.data.invoices : []
    }
  }
  async removePaymentMethod(
    paymentMethodId: string,
    message?: any
  ): Promise<{
    success: boolean
  }> {
    const response = await makeApiRequest(
      { path: '/billing/paymentmethods/remove', method: 'POST', body: { paymentMethodId } },
      message
    )
    if (!response.success) {
      throw new Error(response.data?.message || 'Failed to remove payment method')
    }
    return response.data
  }
}
export interface PaymentMethodInfo {
  id: string
  type?: 'card' | 'au_becs_debit'
  brand: string
  last4: string
  exp_month?: number
  exp_year?: number
  bsb_number?: string
  isDefault?: boolean
}
/** Stripe invoice row for billing history table */
export interface BillingInvoiceRow {
  id: string
  number: string
  created: number
  status: string
  currency: string
  total: number
  amountPaid: number
  invoicePdfUrl: string | null
  hostedInvoiceUrl: string | null
}
export const billingService = new BillingService()
